import type { GetEnvironmentDataResponse } from '@src/gen/ai/h2o/cloud/appstore/v1/ai.h2o.cloud.appstore';
import type { CloudDiscoveryKey, CloudPlatformDiscoveryConfig } from '@shared/contexts';

export type StringEnum = Readonly<Record<string, string>>;

/**
 * What a feature declares about one of its flags. Deliberately tiny: the id
 * lives in the enum, the on/off answer lives in the environment, so a
 * definition only says how this flag relates to others (`parent`) and what the
 * platform must actually be running for the feature to work
 * (`discoveryKeys`).
 *
 * `parent` is written as an id because a feature declares its whole enum in one
 * call and cannot reference a handle that does not exist yet;
 * `defineFeatureFlags` resolves it to the handle.
 */
export type FeatureFlagDefinition<FlagId extends string> = Readonly<{
  parent?: FlagId;
  discoveryKeys?: readonly CloudDiscoveryKey[];
}>;

/**
 * Keyed by every enum member, so adding a flag to the enum without defining it
 * is a compile error rather than a flag that silently evaluates to closed at
 * runtime.
 */
export type FeatureFlagDefinitionMap<Enum extends StringEnum> = Readonly<{
  [MemberName in keyof Enum]: FeatureFlagDefinition<Extract<Enum[keyof Enum], string>>;
}>;

/**
 * Marks a handle as built by `defineFeatureFlags`, so a hand-rolled object
 * fails closed. Without it, `{ id: 'driveEnabled' }` written inline would
 * evaluate perfectly well and quietly bypass the parent chain, the discovery
 * requirements and the definition-time checks that the real handle carries.
 */
export const FEATURE_FLAG_BRAND: unique symbol = Symbol('featureFlagHandle');

/**
 * A malformed definition — duplicate id, missing parent, cyclic parents. Detected
 * once while defining the flags and reported when one is actually evaluated.
 */
export type FeatureFlagProblem = Readonly<{
  reason: 'unknown' | 'cycle';
  flagId?: string;
  message: string;
}>;

export type FeatureFlagHandle<FlagId extends string = string> = Readonly<{
  /** Also the key the environment reports in its `featureFlags` map. */
  id: FlagId;
  discoveryKeys: readonly CloudDiscoveryKey[];
  /** Resolved while defining, so evaluation walks references instead of ids. */
  parent?: FeatureFlagHandle;
  problem?: FeatureFlagProblem;
  [FEATURE_FLAG_BRAND]: true;
}>;

export type FeatureFlagHandles<Enum extends StringEnum> = Readonly<{
  [MemberName in keyof Enum]: FeatureFlagHandle<Extract<Enum[MemberName], string>>;
}>;

/**
 * Everything an evaluation reads. Passing it in rather than reading React
 * context inside the evaluator is what lets the same rule run in a component,
 * in navigation configuration and in a plain unit test.
 *
 * Both members are optional because both arrive asynchronously, and "not here
 * yet" is a distinct answer from "off" — see `FeatureFlagEvaluationResult`.
 */
export type FeatureFlagEvaluationContext = Readonly<{
  env: GetEnvironmentDataResponse | undefined;
  discovery: CloudPlatformDiscoveryConfig | undefined;
}>;

/**
 * Not a boolean, for two reasons. A guard has to tell "still loading" from
 * "denied" — one shows a spinner, the other a 404 — and a denial has to say
 * which flag and which stage, or a feature that vanishes in a deployment is
 * undebuggable. Callers that only want yes/no use `isFeatureOpen`.
 */
export type FeatureFlagEvaluationResult =
  | Readonly<{ status: 'loading'; stage: 'flags' | 'discovery' }>
  | Readonly<{
      status: 'blocked';
      stage: 'flags';
      reason: 'disabled' | 'unknown' | 'cycle';
      flagId?: string;
    }>
  | Readonly<{
      status: 'blocked';
      stage: 'discovery';
      reason: 'unavailable';
      flagId: string;
      missingDiscoveryKeys: readonly CloudDiscoveryKey[];
    }>
  | Readonly<{ status: 'allowed' }>;
